import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchDashboardSummary,
  fetchTeamStatusGrid,
  fetchLocationHeatmap,
  fetchAttendanceTrends,
  fetchGeofenceViolations,
  fetchPendingApprovalsSummary,
  fetchHoursDistribution,
  fetchUserLocationTrail,
} from '../../../../actioncreators/actioncreators';

/**
 * Custom hook for fetching dashboard data
 * Only fetches on mount and manual refresh - no auto-refresh
 * Supports location filtering and org_id for Super Admin
 */
export const useDashboardData = (orgId, locationId = null, subOrgId = null) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const prevLocationId = useRef(locationId);
  const prevOrgId = useRef(orgId);
  const prevSubOrgId = useRef(subOrgId);
  const initialFetch = useRef(false);

  const fetchSummary = useCallback(async (locId, oId, subOId) => {
    try {
      setLoading(true);
      console.log('Fetching dashboard summary with locationId:', locId, 'orgId:', oId);
      const response = await fetchDashboardSummary(locId, oId, subOId);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setSummary(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on initial mount or when orgId/locationId/subOrgId changes
  useEffect(() => {
    const shouldFetch = !initialFetch.current ||
      prevLocationId.current !== locationId ||
      prevOrgId.current !== orgId ||
      prevSubOrgId.current !== subOrgId;

    if (shouldFetch) {
      initialFetch.current = true;
      prevLocationId.current = locationId;
      prevOrgId.current = orgId;
      prevSubOrgId.current = subOrgId;
      fetchSummary(locationId, orgId, subOrgId);
    }
  }, [fetchSummary, locationId, orgId, subOrgId]);

  return { summary, loading, error, refetch: () => fetchSummary(locationId, orgId, subOrgId) };
};

/**
 * Hook for team status grid data
 * Supports location filtering and org_id for Super Admin
 */
export const useTeamStatus = (filters = {}, locationId = null, orgId = null, subOrgId = null) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const prevLocationId = useRef(locationId);
  const prevOrgId = useRef(orgId);
  const prevSubOrgId = useRef(subOrgId);

  const fetchTeamStatus = useCallback(async (locId, oId, subOId) => {
    try {
      setLoading(true);
      const requestBody = { ...filters };
      if (locId) {
        requestBody.location_id = locId;
      }
      if (oId) {
        requestBody.org_id = oId;
      }
      if (subOId) {
        requestBody.sub_org_id = subOId;
      }
      const response = await fetchTeamStatusGrid(requestBody);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setTeamMembers(response.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchTeamStatus(locationId, orgId, subOrgId);
    } else if (prevLocationId.current !== locationId || prevOrgId.current !== orgId || prevSubOrgId.current !== subOrgId) {
      prevLocationId.current = locationId;
      prevOrgId.current = orgId;
      prevSubOrgId.current = subOrgId;
      fetchTeamStatus(locationId, orgId, subOrgId);
    }
  }, [fetchTeamStatus, locationId, orgId, subOrgId]);

  return { teamMembers, loading, error, refetch: () => fetchTeamStatus(locationId, orgId, subOrgId) };
};

/**
 * Hook for location heatmap data
 * Supports location filtering and org_id for Super Admin
 */
export const useLocationHeatmap = (date = null, locationId = null, orgId = null, subOrgId = null) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const dateRef = useRef(date);
  const prevLocationId = useRef(locationId);
  const prevOrgId = useRef(orgId);
  const prevSubOrgId = useRef(subOrgId);

  const fetchLocations = useCallback(async (locId, oId, subOId) => {
    try {
      setLoading(true);
      const requestBody = { date: dateRef.current };
      if (oId) {
        requestBody.org_id = oId;
      }
      if (subOId) {
        requestBody.sub_org_id = subOId;
      }
      const response = await fetchLocationHeatmap(requestBody);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        const allLocations = response.data || [];
        // Filter by location if locationId is provided
        if (locId) {
          const filteredLocations = allLocations.filter(loc => loc.location_id === locId);
          setLocations(filteredLocations);
        } else {
          setLocations(allLocations);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchLocations(locationId, orgId, subOrgId);
    } else if (prevLocationId.current !== locationId || prevOrgId.current !== orgId || prevSubOrgId.current !== subOrgId) {
      prevLocationId.current = locationId;
      prevOrgId.current = orgId;
      prevSubOrgId.current = subOrgId;
      fetchLocations(locationId, orgId, subOrgId);
    }
  }, [fetchLocations, locationId, orgId, subOrgId]);

  return { locations, loading, error, refetch: () => fetchLocations(locationId, orgId, subOrgId) };
};

/**
 * Hook for attendance trends
 * Supports location filtering and org_id for Super Admin
 */
export const useAttendanceTrends = (fromDate, toDate, userIds = [], locationId = null, orgId = null, subOrgId = null) => {
  const [trends, setTrends] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const prevParamsRef = useRef({ fromDate: null, toDate: null, locationId: null, orgId: null, subOrgId: null });

  const fetchTrends = useCallback(async (from, to, locId, oId, subOId) => {
    if (!from || !to) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const requestBody = {
        from_date: from,
        to_date: to,
        user_ids: userIds,
      };
      if (locId) {
        requestBody.location_id = locId;
      }
      if (oId) {
        requestBody.org_id = oId;
      }
      if (subOId) {
        requestBody.sub_org_id = subOId;
      }
      const response = await fetchAttendanceTrends(requestBody);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setTrends(response.data || []);
        setTotalEmployees(response.total_employees || 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userIds]);

  useEffect(() => {
    if (fromDate && toDate &&
        (prevParamsRef.current.fromDate !== fromDate ||
         prevParamsRef.current.toDate !== toDate ||
         prevParamsRef.current.locationId !== locationId ||
         prevParamsRef.current.orgId !== orgId ||
         prevParamsRef.current.subOrgId !== subOrgId)) {
      prevParamsRef.current = { fromDate, toDate, locationId, orgId, subOrgId };
      fetchTrends(fromDate, toDate, locationId, orgId, subOrgId);
    }
  }, [fromDate, toDate, locationId, orgId, subOrgId, fetchTrends]);

  return { trends, totalEmployees, loading, error, refetch: () => fetchTrends(fromDate, toDate, locationId, orgId, subOrgId) };
};

/**
 * Hook for geofence violations
 * Supports location filtering and org_id for Super Admin
 */
export const useGeofenceViolations = (filters = {}, locationId = null, orgId = null, subOrgId = null) => {
  const [violations, setViolations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const prevLocationId = useRef(locationId);
  const prevOrgId = useRef(orgId);
  const prevSubOrgId = useRef(subOrgId);

  const fetchViolations = useCallback(async (locId, oId, subOId) => {
    try {
      setLoading(true);
      const requestBody = {
        from_date: filters.fromDate,
        to_date: filters.toDate,
        user_id: filters.userId,
        resolved: filters.resolved,
        page_number: filters.pageNumber || 1,
        page_size: filters.pageSize || 20,
      };
      if (locId) {
        requestBody.location_id = locId;
      }
      if (oId) {
        requestBody.org_id = oId;
      }
      if (subOId) {
        requestBody.sub_org_id = subOId;
      }
      const response = await fetchGeofenceViolations(requestBody);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setViolations(response.data || []);
        setTotalCount(response.total_count || 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchViolations(locationId, orgId, subOrgId);
    } else if (prevLocationId.current !== locationId || prevOrgId.current !== orgId || prevSubOrgId.current !== subOrgId) {
      prevLocationId.current = locationId;
      prevOrgId.current = orgId;
      prevSubOrgId.current = subOrgId;
      fetchViolations(locationId, orgId, subOrgId);
    }
  }, [fetchViolations, locationId, orgId, subOrgId]);

  return { violations, totalCount, loading, error, refetch: () => fetchViolations(locationId, orgId, subOrgId) };
};

/**
 * Hook for pending approvals summary
 * Supports location filtering and org_id for Super Admin
 */
export const usePendingApprovals = (locationId = null, orgId = null, subOrgId = null) => {
  const [approvals, setApprovals] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const prevLocationId = useRef(locationId);
  const prevOrgId = useRef(orgId);
  const prevSubOrgId = useRef(subOrgId);

  const fetchApprovals = useCallback(async (locId, oId, subOId) => {
    try {
      setLoading(true);
      const response = await fetchPendingApprovalsSummary(locId, oId, subOId);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setApprovals(response.data || []);
        setTotalPending(response.total_pending || 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchApprovals(locationId, orgId, subOrgId);
    } else if (prevLocationId.current !== locationId || prevOrgId.current !== orgId || prevSubOrgId.current !== subOrgId) {
      prevLocationId.current = locationId;
      prevOrgId.current = orgId;
      prevSubOrgId.current = subOrgId;
      fetchApprovals(locationId, orgId, subOrgId);
    }
  }, [fetchApprovals, locationId, orgId, subOrgId]);

  return { approvals, totalPending, loading, error, refetch: () => fetchApprovals(locationId, orgId, subOrgId) };
};

/**
 * Hook for hours distribution
 * Supports location filtering and org_id for Super Admin
 */
export const useHoursDistribution = (fromDate, toDate, locationId = null, orgId = null, subOrgId = null) => {
  const [distribution, setDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const prevParamsRef = useRef({ fromDate: null, toDate: null, locationId: null, orgId: null, subOrgId: null });

  const fetchDistribution = useCallback(async (from, to, locId, oId, subOId) => {
    if (!from || !to) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const requestBody = { from_date: from, to_date: to };
      if (locId) {
        requestBody.location_id = locId;
      }
      if (oId) {
        requestBody.org_id = oId;
      }
      if (subOId) {
        requestBody.sub_org_id = subOId;
      }
      const response = await fetchHoursDistribution(requestBody);
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setDistribution(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fromDate && toDate &&
        (prevParamsRef.current.fromDate !== fromDate ||
         prevParamsRef.current.toDate !== toDate ||
         prevParamsRef.current.locationId !== locationId ||
         prevParamsRef.current.orgId !== orgId ||
         prevParamsRef.current.subOrgId !== subOrgId)) {
      prevParamsRef.current = { fromDate, toDate, locationId, orgId, subOrgId };
      fetchDistribution(fromDate, toDate, locationId, orgId, subOrgId);
    }
  }, [fromDate, toDate, locationId, orgId, subOrgId, fetchDistribution]);

  return { distribution, loading, error, refetch: () => fetchDistribution(fromDate, toDate, locationId, orgId, subOrgId) };
};

/**
 * Hook for user location trail
 */
export const useUserLocationTrail = (userId, date) => {
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const prevParamsRef = useRef({ userId: null, date: null });

  const fetchTrail = useCallback(async (uid, d) => {
    if (!uid || !d) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await fetchUserLocationTrail({ user_id: uid, date: d });
      if (response.businessStatusCode === 2 || response.businessStatusCode === 'SUCCESS') {
        setTrail(response.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId && date &&
        (prevParamsRef.current.userId !== userId || prevParamsRef.current.date !== date)) {
      prevParamsRef.current = { userId, date };
      fetchTrail(userId, date);
    }
  }, [userId, date, fetchTrail]);

  return { trail, loading, error, refetch: () => fetchTrail(userId, date) };
};

export default useDashboardData;
